import { useEffect } from "react";
import { AgendaAppointmentDialog } from "@/components/agenda/AgendaAppointmentDialog";
import { OrganizacaoEmFoco } from "@/components/organization/painel/OrganizacaoEmFoco";
import { useFluxoNovoPedido } from "@/components/prosthesis/FluxoNovoPedido";
import { minutesToTime, timeToMinutes } from "@/lib/agenda-time";
import { trpc } from "@/lib/trpc";

export type AcaoPendente = {
  tipo: "agendar" | "protese";
  organizacaoId: string;
  paciente: { id: string; name: string; phone: string | null };
  /** Muda a cada clique: remonta o fluxo mesmo repetindo paciente e clínica. */
  vez: number;
};

/**
 * Agenda ou pede prótese na clínica do paciente sem trocar a organização ativa: tudo de dentro roda
 * com o cliente daquela clínica (`OrganizacaoEmFoco`), e o cabeçalho de cada fluxo diz qual é.
 */
export function AcaoNaClinica({ acao, onFim }: { acao: AcaoPendente; onFim: () => void }) {
  // Cache da tela principal (agenda pessoal junta consultas e pedidos de todas as clínicas).
  const utilsDeFora = trpc.useUtils();
  const aoMudar = () => {
    utilsDeFora.appointment.invalidate();
    utilsDeFora.prosthesis.invalidate();
  };
  return (
    <OrganizacaoEmFoco key={`${acao.organizacaoId}:${acao.vez}`} organizationId={acao.organizacaoId}>
      {acao.tipo === "agendar" ? <Agendar paciente={acao.paciente} onFim={onFim} aoMudar={aoMudar} /> : <PedirProtese paciente={acao.paciente} aoMudar={aoMudar} />}
    </OrganizacaoEmFoco>
  );
}

function Agendar({ paciente, onFim, aoMudar }: { paciente: AcaoPendente["paciente"]; onFim: () => void; aoMudar: () => void }) {
  const utils = trpc.useUtils();
  const settings = trpc.settings.getAgenda.useQuery().data;
  if (!settings) return null;
  const inicio = timeToMinutes(settings.startHour);
  return (
    <AgendaAppointmentDialog
      open
      mode="create"
      initial={{
        date: new Date(),
        startTime: settings.startHour,
        endTime: minutesToTime(Math.min(inicio + settings.defaultAppointmentDuration, timeToMinutes(settings.endHour))),
        patientId: paciente.id,
        patientName: paciente.name,
        patientPhone: paciente.phone ?? "",
      }}
      settings={settings}
      onClose={onFim}
      onSaved={() => {
        utils.appointment.invalidate();
        aoMudar();
      }}
    />
  );
}

function PedirProtese({ paciente, aoMudar }: { paciente: AcaoPendente["paciente"]; aoMudar: () => void }) {
  const utils = trpc.useUtils();
  const fluxo = useFluxoNovoPedido({
    onConcluido: () => {
      utils.prosthesis.invalidate();
      aoMudar();
    },
  });
  useEffect(() => {
    fluxo.abrirParaPaciente({ id: paciente.id, name: paciente.name });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <>{fluxo.elementos}</>;
}
